import { handleResponse } from "./api";
import { API_BASE_URL } from "../constants/env";
import { getAccessToken, setAccessToken } from "../context/auth/tokenStore";
import { clearSessionHint, setSessionHint } from "../context/auth/sessionHint";
import { withRefreshLock } from "../context/auth/refreshLock";
import { broadcastSession } from "../context/auth/broadcast";
import type { AuthUser } from "../context/auth/types";

const REFRESH_ENDPOINT = "/auth/refresh";

/**
 * The ONE place a refresh-token cookie is ever exchanged for a new access
 * token + user -- AuthProvider's silent-refresh-on-mount and this file's own
 * 401-triggered retry both call `refreshSession()` below and share this
 * exact module-level promise. That's what makes it safe for both to ask at
 * once (e.g. React StrictMode's intentional double-invoke of AuthProvider's
 * mount effect in dev, or some unrelated component's request 401-ing while
 * AuthProvider is still restoring the session on first load): only one
 * POST /auth/refresh ever actually goes out, and every caller just awaits
 * the same in-flight promise instead of starting its own. Previously
 * AuthProvider called authService.refresh() directly while this file had its
 * own separate, uncoordinated refreshPromise -- two independent refreshes
 * could and did fire on the same page load, and only whichever one
 * AuthProvider's own effect happened to be awaiting could ever update its
 * `user` state, so the user could still get logged out even though a
 * refresh (fired from the other, uncoordinated path) genuinely succeeded.
 * withRefreshLock further serializes this across sibling browser TABS
 * sharing the same refresh-token cookie; the module-level promise below
 * only covers callers within this one tab.
 */
let refreshPromise: Promise<{ token: string; user: AuthUser } | null> | null = null;

async function performRefresh(): Promise<{ token: string; user: AuthUser } | null> {
  try {
    const response = await fetch(`${API_BASE_URL}${REFRESH_ENDPOINT}`, {
      method: "POST",
      credentials: "include", // the HttpOnly refresh-token cookie travels here, invisible to JS
      headers: { Accept: "application/json" },
    });
    if (!response.ok) {
      clearSessionHint();
      return null;
    }
    const data = await response.json().catch(() => null);
    const token: string | null = data?.token ?? null;
    const user: AuthUser | null = data?.user ?? null;
    if (!token || !user) {
      clearSessionHint();
      return null;
    }

    // Global side effects of a successful refresh -- applied exactly once,
    // regardless of which caller's request triggered it, since this whole
    // function only ever runs once per in-flight refreshSession() call.
    setAccessToken(token);
    setSessionHint();
    broadcastSession(user, token);

    return { token, user };
  } catch {
    return null;
  }
}

/** Single source of truth for session restoration -- see comment above. */
export function refreshSession(): Promise<{ token: string; user: AuthUser } | null> {
  if (!refreshPromise) {
    refreshPromise = withRefreshLock(performRefresh).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

function handleSessionExpired() {
  setAccessToken(null);
  clearSessionHint();
  try {
    sessionStorage.setItem("arteria_return_to", window.location.pathname + window.location.search);
  } catch {
    /* sessionStorage unavailable */
  }
  window.dispatchEvent(new Event("arteria:session-expired"));
  window.location.href = "/login";
}

type ApiFetchOptions = RequestInit & { auth?: boolean; _retried?: boolean };

const apiFetch = async (endpoint: string, options: ApiFetchOptions = {}): Promise<any> => {
  const { auth = true, _retried = false, ...rest } = options;

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...((rest.headers as Record<string, string>) || {}),
  };

  // Only set Content-Type if not FormData (empty headers object signals FormData)
  if (!(rest.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  if (auth) {
    const token = getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...rest,
    headers,
    // Harmless for endpoints that don't set cookies -- the refresh-token
    // cookie is server-side path-scoped to /api/auth, so this doesn't leak
    // it to unrelated requests, it just means we never have to special-case
    // which calls need it.
    credentials: "include",
  });

  // `auth: false` calls (login, refresh itself, register, ...) never reach
  // here on a 401 they can't recover from -- there's no access token to have
  // expired in the first place, so nothing to refresh. That, plus `_retried`
  // capping every request to exactly one retry, is what bounds this to a
  // single hop and rules out an infinite refresh loop.
  if (response.status === 401 && auth && !_retried) {
    const result = await refreshSession();

    if (result?.token) {
      return apiFetch(endpoint, { ...options, _retried: true });
    }

    handleSessionExpired();
  }

  return handleResponse(response);
};

export default apiFetch;
